"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";

type PublicErrorProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

export default function PublicError({ reset }: PublicErrorProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  function retry() {
    if (isRetrying) return;
    setIsRetrying(true);
    reset();
  }

  return (
    <div
      role="alert"
      aria-labelledby="public-error-heading"
      className="grid min-h-screen place-items-center overflow-x-hidden bg-[#f7f3eb] px-5 py-10 text-[#123b34] sm:px-8"
    >
      <section className="w-full max-w-2xl rounded-[28px] border border-[#e4d7c3] bg-white p-6 shadow-[0_24px_70px_rgba(49,39,24,0.12)] sm:p-10">
        <Link
          href="/"
          aria-label="Singing Attitude UK home"
          className="inline-flex min-h-11 items-center text-sm font-black uppercase tracking-[0.18em] text-[#80611f] underline-offset-4 focus-visible:rounded focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#123b34]"
        >
          Singing Attitude UK
        </Link>

        <p className="mt-8 text-xs font-black uppercase tracking-[0.22em] text-[#9a6f16]">A brief pause</p>
        <h1
          ref={headingRef}
          id="public-error-heading"
          tabIndex={-1}
          className="mt-3 font-serif text-3xl font-semibold leading-tight text-[#123b34] outline-none sm:text-5xl"
        >
          Something went wrong while loading this page.
        </h1>
        <p className="mt-5 max-w-xl text-base font-medium leading-7 text-[#49564f] sm:text-lg">
          Please try again. You can also return to the local site or contact us if the problem continues.
        </p>

        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={retry}
            disabled={isRetrying}
            className="inline-flex min-h-11 w-full items-center justify-center rounded-full bg-[#123b34] px-5 py-3 text-center text-sm font-bold text-white shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-[#c99422] focus-visible:ring-offset-2 disabled:cursor-wait disabled:bg-[#536b64]"
          >
            {isRetrying ? "Trying again…" : "Retry this page"}
          </button>
          <Link
            href="/local-lessons"
            className="inline-flex min-h-11 w-full items-center justify-center rounded-full border border-[#c99422] bg-[#f4dfad] px-5 py-3 text-center text-sm font-bold text-[#382b12] outline-none focus-visible:ring-2 focus-visible:ring-[#123b34] focus-visible:ring-offset-2"
          >
            Explore local singing lessons
          </Link>
        </div>

        <nav aria-label="Error recovery" className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
          <Link
            href="/"
            className="inline-flex min-h-11 items-center text-sm font-bold text-[#123b34] underline decoration-[#c99422] decoration-2 underline-offset-4 focus-visible:rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#123b34]"
          >
            Return home
          </Link>
          <Link
            href="/contact"
            className="inline-flex min-h-11 items-center text-sm font-bold text-[#123b34] underline decoration-[#c99422] decoration-2 underline-offset-4 focus-visible:rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#123b34]"
          >
            Contact Singing Attitude UK
          </Link>
        </nav>
      </section>
    </div>
  );
}
